import { editor as MonacoEditor, Range } from 'monaco-editor';
import type { ActionItem } from '../../../redux/EditorSlice';
import type { SensorActionDraft, SensorMappingDraft, TriggerDraft } from './SensorMappingDialog';
import {
  formatAutonomousActionExpression,
  findActionDefForLabel,
  findActionItemForLabel,
  parseAutonomousExpression,
  splitTopLevelByComma,
} from './driveModeAutonomousActionUtils';
import {
  clampInsertLineBeforeRobotStart,
  escapeRegex,
  findInputMappingsRegionBlockBounds,
  findOrCreateRegionBlock,
  findRobotStartLine,
  getInsertLineBeforeRobotStart,
  getLastMappingScanLineBeforeRobotStart,
  getRemoveEmptyInputMappingsRegionEdit,
} from '../../../utils/monacoRegionUtils';
import { INPUT_MAPPINGS_REGION_START, REGION_END } from '../../../utils/programRegionMarkers';
import {
  bumpInsertLineForTopLevelVarDeclarationAnchors,
  findTopLevelKeywordAssignmentIndex,
  findTopLevelVarDeclarationEndLine,
  findTopLevelVarDeclarationEndLineForAnchor,
  isTopLevelCodeLine,
  readBalancedDelimiterSpan,
  type TopLevelVarDeclarationAnchor,
} from '../../../utils/pythonSourceScanUtils';
import { getFrameworkActionTargetAllowedClassNames, getFrameworkSensorClassNames } from '../../../utils/frameworkClassNameSets';
import { isPlainFrameworkNumericLiteral } from '../../../utils/frameworkArgTypes';
import { formatGeneratedPythonCall } from '../../../utils/pythonFormattingUtils';
import { applyMainProgramEdits, getMainProgramEditorProxy } from '../utils/mainProgramEditor';
import {
  DISTANCE_TRIGGER_NAMES,
  SENSOR_ACTION_SIDES,
  SENSOR_TRIGGER_ARG_KEYS,
  SENSOR_TRIGGER_NAMES,
  type SensorActionSide,
  type SensorTriggerName,
} from './sensorMappingConstants';

export { SENSOR_ACTION_SIDES };
export type { SensorActionSide };

type ActionTargetDeclarationAnchor = TopLevelVarDeclarationAnchor;

function getAllowedClassNamesForSensorActionTarget(actionLabel: string, actionList: ActionItem[]): ReadonlySet<string> | undefined {
  const actionItem = findActionItemForLabel(actionList || [], actionLabel);
  return getFrameworkActionTargetAllowedClassNames(actionItem?.mechanismClass?.name ?? actionItem?.parentClass?.name);
}

function parseKwargs(argsText: string): Record<string, string> {
  const text = String(argsText || '').trim();
  if (!text) return {};
  const out: Record<string, string> = {};
  splitTopLevelByComma(text).forEach((entry) => {
    const eq = findTopLevelKeywordAssignmentIndex(entry);
    if (eq < 0) return;
    const k = String(entry.slice(0, eq) ?? '').trim();
    const v = String(entry.slice(eq + 1) ?? '').trim();
    if (!/^[A-Za-z_][\w]*$/.test(k)) return;
    out[k] = v;
  });
  return out;
}

function isPlainNumber(s: string): boolean {
  return isPlainFrameworkNumericLiteral(s);
}

function shouldPreserveRawTriggerArgs(triggerName: string, rawArgsText: string, args: Record<string, string>): boolean {
  const raw = String(rawArgsText || '').trim();
  if (!raw) return false;
  if (!DISTANCE_TRIGGER_NAMES.has(triggerName as SensorTriggerName)) return false;
  if (splitTopLevelByComma(raw).some((entry) => findTopLevelKeywordAssignmentIndex(entry) < 0)) return true;

  const distanceArgNames =
    triggerName === SENSOR_TRIGGER_NAMES.GREATER_THAN || triggerName === SENSOR_TRIGGER_NAMES.LESS_THAN
      ? [SENSOR_TRIGGER_ARG_KEYS.DISTANCE]
      : [SENSOR_TRIGGER_ARG_KEYS.DISTANCE, SENSOR_TRIGGER_ARG_KEYS.DISTANCE_NEAR, SENSOR_TRIGGER_ARG_KEYS.DISTANCE_FAR];

  return distanceArgNames.some((name) => {
    const value = String(args[name] ?? '').trim();
    return value.length > 0 && !isPlainNumber(value);
  });
}

function stableArgsKey(args: Record<string, string>, rawArgsText?: string): string {
  const raw = String(rawArgsText || '').trim();
  if (raw) return JSON.stringify({ raw });
  return JSON.stringify(
    Object.entries(args ?? {})
      .filter(([k, v]) => String(k || '').trim() && String(v ?? '').trim())
      .sort(([a], [b]) => (a < b ? -1 : a > b ? 1 : 0))
  );
}

function normalizeActionDraft(action: Partial<SensorActionDraft> | null | undefined): SensorActionDraft | null {
  const actionLabel = String(action?.actionLabel ?? '').trim();
  const rawExpression = String(action?.rawExpression ?? '').trim();
  if (!actionLabel && !rawExpression) return null;
  return {
    actionLabel,
    argValues: (action?.argValues ?? {}) as Record<string, string>,
    rawExpression: rawExpression || undefined,
  };
}

function actionsForSide(
  tr: TriggerDraft,
  actionsKey: 'onTrueActions' | 'onFalseActions',
  legacyLabelKey: 'onTrueActionLabel' | 'onFalseActionLabel',
  legacyArgsKey: 'onTrueArgValues' | 'onFalseArgValues'
): SensorActionDraft[] {
  const actions = tr[actionsKey];
  if (Array.isArray(actions)) {
    return actions.map((action) => normalizeActionDraft(action)).filter((action): action is SensorActionDraft => action !== null);
  }

  const legacyAction = normalizeActionDraft({
    actionLabel: tr[legacyLabelKey],
    argValues: tr[legacyArgsKey],
  });
  return legacyAction ? [legacyAction] : [];
}

function withLegacyActionFields(
  tr: Omit<TriggerDraft, 'onTrueActionLabel' | 'onFalseActionLabel' | 'onTrueArgValues' | 'onFalseArgValues'>
): TriggerDraft {
  const onTrueFirst = tr.onTrueActions?.[0];
  const onFalseFirst = tr.onFalseActions?.[0];
  return {
    ...tr,
    onTrueActionLabel: onTrueFirst?.actionLabel ?? '',
    onFalseActionLabel: onFalseFirst?.actionLabel ?? '',
    onTrueArgValues: onTrueFirst?.argValues ?? {},
    onFalseArgValues: onFalseFirst?.argValues ?? {},
  };
}

function parseSensorActionExpression(expr: string): SensorActionDraft | null {
  const parsed = parseAutonomousExpression(expr);
  const actionLabel = String(parsed.selection || '').trim();
  const rawExpression = String(parsed.rawExpression || '').trim();
  if (!actionLabel && !rawExpression) return null;
  return {
    actionLabel,
    argValues: parsed.argValues,
    rawExpression: actionLabel ? undefined : rawExpression,
  };
}

function normalizeTriggerDrafts(triggers: TriggerDraft[]): TriggerDraft[] {
  const triggersByKey = new Map<string, TriggerDraft>();

  for (const tr of triggers || []) {
    const triggerName = String(tr.triggerName || '').trim();
    if (!triggerName) continue;

    const triggerArgs = (tr.triggerArgs ?? {}) as Record<string, string>;
    const rawTriggerArgsText = String(tr.rawTriggerArgsText ?? '').trim() || undefined;
    const key = `${triggerName}::${stableArgsKey(triggerArgs, rawTriggerArgsText)}`;
    const onTrueActions = actionsForSide(tr, 'onTrueActions', 'onTrueActionLabel', 'onTrueArgValues');
    const onFalseActions = actionsForSide(tr, 'onFalseActions', 'onFalseActionLabel', 'onFalseArgValues');

    const prev = triggersByKey.get(key);
    const next = prev
      ? withLegacyActionFields({
          ...prev,
          onTrueActions: [...(prev.onTrueActions ?? []), ...onTrueActions],
          onFalseActions: [...(prev.onFalseActions ?? []), ...onFalseActions],
        })
      : withLegacyActionFields({
          triggerName,
          triggerArgs,
          rawTriggerArgsText,
          onTrueActions,
          onFalseActions,
        });

    triggersByKey.set(key, next);
  }

  return Array.from(triggersByKey.values()).filter((tr) => (tr.onTrueActions?.length ?? 0) > 0 || (tr.onFalseActions?.length ?? 0) > 0);
}

function parseTriggerChainLine(
  rawLine: string,
  sensorVarName: string
): {
  triggerName: string;
  triggerArgsText: string;
  onTrueExprs: string[];
  onFalseExprs: string[];
} | null {
  const line = String(rawLine || '').trim();
  if (!line) return null;
  if (!line.startsWith(`${sensorVarName}.`)) return null;

  let i = sensorVarName.length + 1; // after "<sensor>."
  const nameMatch = line.slice(i).match(/^([A-Za-z_][\w]*)/);
  if (!nameMatch) return null;
  const triggerName = nameMatch[1] ?? '';
  i += triggerName.length;
  if (line[i] !== '(') return null;

  const baseArgs = readBalancedDelimiterSpan(line, i, '(', ')', { ignoreLineComments: true });
  if (!baseArgs) return null;
  const triggerArgsText = baseArgs.inner;
  i = baseArgs.closeIndex + 1;

  const onTrueExprs: string[] = [];
  const onFalseExprs: string[] = [];

  while (i < line.length) {
    if (line[i] !== '.') break;
    i++;
    const segMatch = line.slice(i).match(/^([A-Za-z_][\w]*)/);
    if (!segMatch) break;
    const segName = segMatch[1] ?? '';
    i += segName.length;
    if (line[i] !== '(') break;
    const segArgs = readBalancedDelimiterSpan(line, i, '(', ')', { ignoreLineComments: true });
    if (!segArgs) break;
    const segInner = segArgs.inner.trim();
    i = segArgs.closeIndex + 1;

    if (segName === SENSOR_ACTION_SIDES.ON_TRUE && segInner) onTrueExprs.push(segInner);
    if (segName === SENSOR_ACTION_SIDES.ON_FALSE && segInner) onFalseExprs.push(segInner);
  }

  if (onTrueExprs.length === 0 && onFalseExprs.length === 0) return null;
  return { triggerName, triggerArgsText, onTrueExprs, onFalseExprs };
}

function hasUnclosedParentheses(text: string): boolean {
  let depth = 0;
  let quote: '"' | "'" | null = null;
  let escaped = false;

  for (let i = 0; i < text.length; i++) {
    const ch = text[i] ?? '';
    if (quote) {
      if (escaped) {
        escaped = false;
      } else if (ch === '\\') {
        escaped = true;
      } else if (ch === quote) {
        quote = null;
      }
      continue;
    }

    if (ch === '"' || ch === "'") {
      quote = ch;
      continue;
    }
    if (ch === '#') {
      while (i < text.length && text[i] !== '\n' && text[i] !== '\r') i++;
      continue;
    }
    if (ch === '(') depth++;
    if (ch === ')') depth = Math.max(0, depth - 1);
  }

  return depth > 0;
}

function readTriggerChainSpan(
  model: MonacoEditor.ITextModel,
  startLine: number,
  sensorVarName: string
): { parsed: NonNullable<ReturnType<typeof parseTriggerChainLine>>; endLine: number } | null {
  const firstLine = model.getLineContent(startLine);
  if (
    !String(firstLine || '')
      .trim()
      .startsWith(`${sensorVarName}.`)
  )
    return null;

  let source = '';
  for (let lineNumber = startLine; lineNumber <= model.getLineCount(); lineNumber++) {
    source = source ? `${source}\n${model.getLineContent(lineNumber)}` : model.getLineContent(lineNumber);
    const parsed = parseTriggerChainLine(source, sensorVarName);
    if (parsed) return { parsed, endLine: lineNumber };
    if (!hasUnclosedParentheses(source)) return null;
  }

  return null;
}

export function readSensorMappingDraftFromCode(opts: {
  editor: MonacoEditor.IStandaloneCodeEditor;
  sensorVarName: string;
}): SensorMappingDraft | null {
  const { editor, sensorVarName } = opts;
  const model = getMainProgramEditorProxy(editor)?.getModel();
  if (!model) return null;

  const lineCount = model.getLineCount();
  const triggersByKey = new Map<string, TriggerDraft>();
  const minScanLine = getEffectiveSensorMappingStartLine(model, sensorVarName);

  for (let ln = minScanLine; ln <= lineCount; ln++) {
    const line = model.getLineContent(ln);
    if (!isTopLevelCodeLine(line)) continue;
    const chainSpan = readTriggerChainSpan(model, ln, sensorVarName);
    if (!chainSpan) continue;
    const { parsed } = chainSpan;
    ln = chainSpan.endLine;

    const triggerArgs = parseKwargs(parsed.triggerArgsText);
    const rawTriggerArgsText = shouldPreserveRawTriggerArgs(parsed.triggerName, parsed.triggerArgsText, triggerArgs)
      ? parsed.triggerArgsText.trim()
      : undefined;
    const key = `${parsed.triggerName}::${stableArgsKey(triggerArgs, rawTriggerArgsText)}`;

    const prev = triggersByKey.get(key);
    const next: TriggerDraft = prev
      ? {
          ...prev,
          onTrueActions: [...(prev.onTrueActions ?? [])],
          onFalseActions: [...(prev.onFalseActions ?? [])],
        }
      : withLegacyActionFields({
          triggerName: parsed.triggerName,
          triggerArgs,
          rawTriggerArgsText,
          onTrueActions: [],
          onFalseActions: [],
        });

    const onTrueActions = parsed.onTrueExprs
      .map((expr) => parseSensorActionExpression(expr))
      .filter((action): action is SensorActionDraft => action !== null);
    const onFalseActions = parsed.onFalseExprs
      .map((expr) => parseSensorActionExpression(expr))
      .filter((action): action is SensorActionDraft => action !== null);

    if (onTrueActions.length > 0) {
      next.onTrueActions = [...(next.onTrueActions ?? []), ...onTrueActions];
    }
    if (onFalseActions.length > 0) {
      next.onFalseActions = [...(next.onFalseActions ?? []), ...onFalseActions];
    }

    triggersByKey.set(key, withLegacyActionFields(next));
  }

  const triggers = Array.from(triggersByKey.values());
  return triggers.length > 0 ? { triggers } : null;
}

function getActionTargetDeclarationNameFromActionLabel(actionLabel: string): string | null {
  const trimmed = String(actionLabel || '').trim();
  if (!trimmed || trimmed.toLowerCase().startsWith('custom.')) return null;
  const parentName = trimmed.split('.')[0];
  if (!parentName || parentName === 'UtilityActions') return null;
  return parentName;
}

function collectActionTargetDeclarationAnchorsFromDraft(draft: SensorMappingDraft, actionList: ActionItem[]): ActionTargetDeclarationAnchor[] {
  const anchors = new Map<string, ActionTargetDeclarationAnchor>();
  for (const tr of normalizeTriggerDrafts(draft.triggers || [])) {
    for (const action of [
      ...actionsForSide(tr, 'onTrueActions', 'onTrueActionLabel', 'onTrueArgValues'),
      ...actionsForSide(tr, 'onFalseActions', 'onFalseActionLabel', 'onFalseArgValues'),
    ]) {
      const actionLabel = String(action.actionLabel || '').trim();
      const variableName = getActionTargetDeclarationNameFromActionLabel(actionLabel);
      if (!variableName) continue;
      const anchor: ActionTargetDeclarationAnchor = {
        variableName,
        allowedClassNames: getAllowedClassNamesForSensorActionTarget(actionLabel, actionList),
      };
      const existing = anchors.get(variableName);
      if (!existing?.allowedClassNames || anchor.allowedClassNames) {
        anchors.set(variableName, anchor);
      }
    }
  }
  return Array.from(anchors.values());
}

function isLineInDeletedChunk(line: number, chunks: ReadonlyArray<{ start: number; end: number }>): boolean {
  return chunks.some((chunk) => line >= chunk.start && line < chunk.end);
}

function clampInsertLineOutsideDeletedChunks(insertLine: number, chunks: ReadonlyArray<{ start: number; end: number }>): number {
  let line = insertLine;
  let changed = true;
  while (changed) {
    changed = false;
    for (const chunk of chunks) {
      if (line >= chunk.start && line < chunk.end) {
        line = chunk.end;
        changed = true;
      }
    }
  }
  return line;
}

function getLastTopLevelSensorMappingLine(
  model: MonacoEditor.ITextModel,
  sensorVarName: string,
  excludeChunks: ReadonlyArray<{ start: number; end: number }> = [],
  scanRange?: { startLine: number; endLine: number }
): number {
  const mappingRe = new RegExp(String.raw`^\s*${escapeRegex(sensorVarName)}\.`);
  const maxScanLine = scanRange?.endLine ?? getLastMappingScanLineBeforeRobotStart(model);
  const minScanLine = scanRange?.startLine ?? 1;
  let lastLine = 0;
  for (let i = minScanLine; i <= maxScanLine; i++) {
    if (isLineInDeletedChunk(i, excludeChunks)) continue;
    const line = model.getLineContent(i);
    if (!isTopLevelCodeLine(line)) continue;
    if (!mappingRe.test(line)) continue;
    const chainSpan = readTriggerChainSpan(model, i, sensorVarName);
    if (chainSpan) {
      lastLine = chainSpan.endLine;
      i = chainSpan.endLine;
    } else {
      lastLine = i;
    }
  }
  return lastLine;
}

function getEffectiveSensorDeclarationEndLine(model: MonacoEditor.ITextModel, sensorVarName: string): number | null {
  return findTopLevelVarDeclarationEndLine(model, sensorVarName, {
    allowedClassNames: getFrameworkSensorClassNames(),
  });
}

function getEffectiveSensorMappingStartLine(model: MonacoEditor.ITextModel, sensorVarName: string): number {
  const sensorDeclEndLine = getEffectiveSensorDeclarationEndLine(model, sensorVarName);
  return sensorDeclEndLine !== null ? sensorDeclEndLine + 1 : 1;
}

function partitionSensorMappingChunks(
  model: MonacoEditor.ITextModel,
  sensorVarName: string,
  chunks: ReadonlyArray<{ start: number; end: number }>
): { activeChunks: Array<{ start: number; end: number }>; staleChunks: Array<{ start: number; end: number }> } {
  const minActiveMappingLine = getEffectiveSensorMappingStartLine(model, sensorVarName);
  const activeChunks: Array<{ start: number; end: number }> = [];
  const staleChunks: Array<{ start: number; end: number }> = [];
  for (const chunk of chunks) {
    if (chunk.start < minActiveMappingLine) staleChunks.push(chunk);
    else activeChunks.push(chunk);
  }
  return { activeChunks, staleChunks };
}

function isLineInsideInputMappingsRegion(line: number, regionBounds: { startLine: number; endLine: number }): boolean {
  return line > regionBounds.startLine && line < regionBounds.endLine;
}

function pushChunkDeletionEdits(edits: MonacoEditor.IIdentifiedSingleEditOperation[], chunks: ReadonlyArray<{ start: number; end: number }>) {
  for (const chunk of chunks) {
    edits.push({ range: new Range(chunk.start, 1, chunk.end, 1), text: '', forceMoveMarkers: true });
  }
}

function getInsertLineInsideInputMappingsRegion(
  model: MonacoEditor.ITextModel,
  sensorVarName: string,
  regionBounds: { startLine: number; endLine: number },
  excludeChunks: ReadonlyArray<{ start: number; end: number }>
): number {
  const lastMappingInRegion = getLastTopLevelSensorMappingLine(model, sensorVarName, excludeChunks, {
    startLine: regionBounds.startLine + 1,
    endLine: regionBounds.endLine - 1,
  });
  if (lastMappingInRegion > 0) {
    return Math.min(lastMappingInRegion + 1, regionBounds.endLine);
  }
  return regionBounds.endLine;
}

function collectSensorMappingChunks(model: MonacoEditor.ITextModel, sensorVarName: string): Array<{ start: number; end: number }> {
  const chunks: Array<{ start: number; end: number }> = [];
  const lineCount = model.getLineCount();

  for (let ln = 1; ln <= lineCount; ln++) {
    const line = model.getLineContent(ln);
    if (!isTopLevelCodeLine(line)) continue;
    const chainSpan = readTriggerChainSpan(model, ln, sensorVarName);
    if (!chainSpan) continue;
    const end =
      chainSpan.endLine < lineCount && !String(model.getLineContent(chainSpan.endLine + 1) || '').trim()
        ? chainSpan.endLine + 2
        : chainSpan.endLine + 1;
    chunks.push({ start: ln, end });
    ln = end - 1;
  }

  return chunks;
}

function bumpInsertLineForActionTargetAnchors(
  model: MonacoEditor.ITextModel,
  insertLine: number,
  anchors: ActionTargetDeclarationAnchor[],
  regionBounds: { startLine: number; endLine: number } | null
): number {
  let line = bumpInsertLineForTopLevelVarDeclarationAnchors(model, insertLine, anchors);
  if (!regionBounds) return line;
  for (const anchor of anchors) {
    const targetEndLine = findTopLevelVarDeclarationEndLineForAnchor(model, anchor);
    if (targetEndLine !== null && isLineInsideInputMappingsRegion(targetEndLine, regionBounds)) {
      line = Math.min(line, regionBounds.endLine);
    }
  }
  return line;
}

function getMaxDeclaredActionTargetInsertLine(model: MonacoEditor.ITextModel, draft: SensorMappingDraft, actionList: ActionItem[]): number {
  return bumpInsertLineForTopLevelVarDeclarationAnchors(model, 0, collectActionTargetDeclarationAnchorsFromDraft(draft, actionList));
}

function hasSensorMappingChunkOnOrAfterRobotStart(model: MonacoEditor.ITextModel, chunks: ReadonlyArray<{ start: number }>): boolean {
  const startLine = findRobotStartLine(model);
  if (startLine === null) return false;
  return chunks.some((chunk) => chunk.start >= startLine);
}

/** 1-based line for the next sensor trigger mapping inside the input-mappings region when possible. */
function getInsertLineForSensorMapping(
  editor: MonacoEditor.IStandaloneCodeEditor,
  sensorVarName: string,
  draft: SensorMappingDraft,
  actionList: ActionItem[],
  excludeChunks: ReadonlyArray<{ start: number; end: number }> = []
): number {
  const targetEditor = getMainProgramEditorProxy(editor) ?? editor;
  const model = targetEditor.getModel();
  if (!model) return 1;

  const regionBounds = findInputMappingsRegionBlockBounds(model);
  const sensorDeclEndLine = getEffectiveSensorDeclarationEndLine(model, sensorVarName);
  const sensorDeclInsideRegion =
    regionBounds !== null && sensorDeclEndLine !== null && isLineInsideInputMappingsRegion(sensorDeclEndLine, regionBounds);

  let insertLine: number;
  if (regionBounds && (sensorDeclEndLine === null || sensorDeclInsideRegion)) {
    insertLine = getInsertLineInsideInputMappingsRegion(model, sensorVarName, regionBounds, excludeChunks);
    if (sensorDeclEndLine !== null) {
      insertLine = Math.max(insertLine, sensorDeclEndLine + 1);
      insertLine = Math.min(insertLine, regionBounds.endLine);
    }
  } else if (sensorDeclEndLine !== null) {
    insertLine = Math.max(sensorDeclEndLine + 1, getLastTopLevelSensorMappingLine(model, sensorVarName, excludeChunks) + 1);
  } else {
    return findOrCreateSensorBlock(targetEditor).endLine;
  }

  const actionTargetAnchors = collectActionTargetDeclarationAnchorsFromDraft(draft, actionList);
  insertLine = bumpInsertLineForActionTargetAnchors(model, insertLine, actionTargetAnchors, regionBounds);

  insertLine = clampInsertLineBeforeRobotStart(model, insertLine);
  if (excludeChunks.length > 0) {
    insertLine = clampInsertLineOutsideDeletedChunks(insertLine, excludeChunks);
    insertLine = clampInsertLineBeforeRobotStart(model, insertLine);
  }
  return insertLine;
}

function applySensorMappingEdits(
  targetEditor: MonacoEditor.IStandaloneCodeEditor,
  source: string,
  edits: MonacoEditor.IIdentifiedSingleEditOperation[]
) {
  applyMainProgramEdits(targetEditor, source, edits);
  const model = getMainProgramEditorProxy(targetEditor)?.getModel() ?? targetEditor.getModel();
  const cleanupEdit = model ? getRemoveEmptyInputMappingsRegionEdit(model) : null;
  if (cleanupEdit) {
    applyMainProgramEdits(targetEditor, 'inputMappings.region.cleanup', [cleanupEdit], { undoStop: false, focusEditor: false });
  }
}

function findOrCreateSensorBlock(editor: MonacoEditor.IStandaloneCodeEditor): { startLine: number; endLine: number } {
  const targetEditor = getMainProgramEditorProxy(editor);
  const model = targetEditor?.getModel();
  if (!model) return { startLine: 1, endLine: 1 };

  const insertBeforeStart = getInsertLineBeforeRobotStart(model);
  const insertLine = insertBeforeStart ?? model.getLineCount() + 1;
  return findOrCreateRegionBlock(targetEditor ?? editor, {
    startMarker: INPUT_MAPPINGS_REGION_START,
    endMarker: REGION_END,
    insertLine,
    editSource: 'sensor.mappings.block',
  });
}

// Intentionally no longer emitting commented mappings; we generate real Python in `upsertSensorMappingInCode`.

export function upsertSensorMappingInCode(opts: {
  editor: MonacoEditor.IStandaloneCodeEditor;
  sensorVarName: string;
  draft: SensorMappingDraft;
  actionList: ActionItem[];
}) {
  const { editor, sensorVarName, draft, actionList } = opts;
  const targetEditor = getMainProgramEditorProxy(editor);
  const model = targetEditor?.getModel();
  if (!model) return;

  // Find existing mapping chunks for this sensor by locating any `<sensorVarName>.<trigger>(...).on_true(...)` chain anywhere in the program.
  // We intentionally do NOT rely on special marker comments: users may delete them or hand-edit code.
  const chunks = collectSensorMappingChunks(model, sensorVarName);
  const { activeChunks, staleChunks } = partitionSensorMappingChunks(model, sensorVarName, chunks);

  const findActionDef = (label: string) => findActionDefForLabel(actionList || [], label);
  const lines: string[] = [];

  for (const tr of normalizeTriggerDrafts(draft.triggers || [])) {
    const name = String(tr.triggerName || '').trim();
    if (!name) continue;
    const rawTriggerArgsText = String(tr.rawTriggerArgsText || '').trim();
    const args = tr.triggerArgs ?? {};
    const argsText =
      rawTriggerArgsText ||
      Object.entries(args)
        .filter(([k, v]) => String(k || '').trim() && String(v ?? '').trim())
        .map(([k, v]) => `${k}=${String(v ?? '').trim()}`)
        .join(', ');

    const base = `${sensorVarName}.${name}(${argsText})`;
    const onTrueActions = actionsForSide(tr, 'onTrueActions', 'onTrueActionLabel', 'onTrueArgValues');
    const onFalseActions = actionsForSide(tr, 'onFalseActions', 'onFalseActionLabel', 'onFalseArgValues');

    if (onTrueActions.length === 0 && onFalseActions.length === 0) continue;

    // Emit separate statements so users can easily scan/hand-edit and so on_true/on_false
    // don't become a single long chained expression.
    for (const action of onTrueActions) {
      const expr = formatAutonomousActionExpression(action.actionLabel, action.argValues, findActionDef, { unsupportedCode: action.rawExpression });
      if (expr && expr !== 'None') lines.push(formatGeneratedPythonCall(`${base}.on_true(${expr})`));
    }
    for (const action of onFalseActions) {
      const expr = formatAutonomousActionExpression(action.actionLabel, action.argValues, findActionDef, { unsupportedCode: action.rawExpression });
      if (expr && expr !== 'None') lines.push(formatGeneratedPythonCall(`${base}.on_false(${expr})`));
    }
    lines.push('');
  }

  const edits: MonacoEditor.IIdentifiedSingleEditOperation[] = [];
  const newLines = lines.join('\n');

  if (lines.length === 0) {
    if (chunks.length === 0) return;
    pushChunkDeletionEdits(edits, chunks);
    applySensorMappingEdits(targetEditor ?? editor, 'sensor.mappings.upsert', edits);
    return;
  }

  if (activeChunks.length > 0 || staleChunks.length > 0) {
    const insertLine = getInsertLineForSensorMapping(targetEditor ?? editor, sensorVarName, draft, actionList, chunks);
    const actionTargetInsertLine = getMaxDeclaredActionTargetInsertLine(model, draft, actionList);
    // `activeChunks[0].end` is an exclusive Monaco range end (a one-line chunk is `{ start: 2, end: 3 }`).
    const needsRelocation =
      staleChunks.length > 0 ||
      hasSensorMappingChunkOnOrAfterRobotStart(model, activeChunks) ||
      (activeChunks.length > 0 && actionTargetInsertLine > 0 && actionTargetInsertLine > activeChunks[0]!.end);

    if (!needsRelocation) {
      edits.push({ range: new Range(activeChunks[0]!.start, 1, activeChunks[0]!.end, 1), text: newLines, forceMoveMarkers: true });
      pushChunkDeletionEdits(edits, activeChunks.slice(1));
      pushChunkDeletionEdits(edits, staleChunks);
    } else {
      pushChunkDeletionEdits(edits, chunks);
      edits.push({ range: new Range(insertLine, 1, insertLine, 1), text: newLines, forceMoveMarkers: true });
    }
  } else {
    const insertLine = getInsertLineForSensorMapping(targetEditor ?? editor, sensorVarName, draft, actionList);
    edits.push({ range: new Range(insertLine, 1, insertLine, 1), text: newLines, forceMoveMarkers: true });
  }

  applySensorMappingEdits(targetEditor ?? editor, 'sensor.mappings.upsert', edits);
}
